<template>
  <section class="dp-layout class-circle-config-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>
    <section class="dp-right config-page-right">
      <toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="searchList"></toolbar>

      <div v-if="!schoolId" class="empty-tip">
        <div class="empty-tip-inner">
          <i class="el-icon-school empty-icon"></i>
          <p class="empty-title">尚未选择学校</p>
          <p class="empty-desc">请从左侧机构树选择学校节点后再配置班级圈</p>
        </div>
      </div>

      <div v-else class="config-panel" v-loading="loading">
        <div class="config-top-bar">
          <div class="org-display-card">
            <i class="el-icon-office-building org-icon"></i>
            <span class="org-label">当前学校：</span>
            <span class="org-value">{{ currentSchoolName || "—" }}</span>
          </div>
          <el-button
            type="primary"
            icon="el-icon-check"
            :loading="saving"
            :disabled="!schoolId"
            @click="saveConfig"
          >保存配置</el-button>
        </div>

        <div class="setting-grid">
          <div class="setting-grid-left">
            <div class="setting-card setting-card--audit">
              <header class="setting-card-head">
                <span class="setting-card-icon setting-card-icon--audit"><i class="el-icon-view"></i></span>
                <div class="setting-card-head-text">
                  <span class="setting-card-name">审核</span>
                  <span class="setting-card-sub">控制家长发帖是否进入待审</span>
                </div>
              </header>
              <div class="setting-row">
                <div class="setting-row-text">
                  <div class="setting-row-label">家长发帖需审核</div>
                  <div class="setting-row-desc">开启后须班主任或后台审核通过，帖子才对全班可见</div>
                </div>
                <el-switch v-model="configForm.parentPostNeedApproval" class="setting-row-switch"></el-switch>
              </div>
            </div>

            <div class="setting-card setting-card--push">
              <header class="setting-card-head">
                <span class="setting-card-icon setting-card-icon--push"><i class="el-icon-bell"></i></span>
                <div class="setting-card-head-text">
                  <span class="setting-card-name">推送总开关</span>
                  <span class="setting-card-sub">关闭后下方分项均不发送</span>
                </div>
              </header>
              <div class="setting-row">
                <div class="setting-row-text">
                  <div class="setting-row-label">班级圈推送</div>
                  <div class="setting-row-desc">家长走微信或钉钉由机构「家长微信推送」配置决定</div>
                </div>
                <el-switch v-model="configForm.pushEnabled" class="setting-row-switch"></el-switch>
              </div>
            </div>
          </div>

          <div class="setting-grid-right">
            <div class="setting-card setting-card--sub setting-card--stretch" :class="{ 'is-muted': !configForm.pushEnabled }">
              <header class="setting-card-head">
                <span class="setting-card-icon setting-card-icon--sub"><i class="el-icon-message"></i></span>
                <div class="setting-card-head-text">
                  <span class="setting-card-name">推送分项</span>
                  <span class="setting-card-sub">按场景单独控制是否通知</span>
                </div>
              </header>

              <div class="setting-list">
                <div class="setting-row setting-row--list">
                  <div class="setting-row-text">
                    <div class="setting-row-label">
                      待审通知班主任
                      <span class="setting-tag">钉钉</span>
                    </div>
                  </div>
                  <el-switch v-model="configForm.pendingAuditPushEnabled" :disabled="!configForm.pushEnabled" class="setting-row-switch"></el-switch>
                </div>

                <div class="setting-row setting-row--list">
                  <div class="setting-row-text">
                    <div class="setting-row-label">审核结果推送给家长</div>
                  </div>
                  <el-switch v-model="configForm.auditResultPushEnabled" :disabled="!configForm.pushEnabled" class="setting-row-switch"></el-switch>
                </div>

                <div class="setting-row setting-row--list">
                  <div class="setting-row-text">
                    <div class="setting-row-label">本班新动态推送给其他家长</div>
                  </div>
                  <el-switch v-model="configForm.newPostPushEnabled" :disabled="!configForm.pushEnabled" class="setting-row-switch"></el-switch>
                </div>

                <div class="setting-row setting-row--list setting-row--last">
                  <div class="setting-row-text">
                    <div class="setting-row-label">评论 / 回复推送</div>
                  </div>
                  <el-switch v-model="configForm.commentPushEnabled" :disabled="!configForm.pushEnabled" class="setting-row-switch"></el-switch>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from "../../components/DepartmentPane";
import Toolbar from "../../components/Toolbar";
import { getButtonList } from "../../promissionRouter";
import {
  getClassCircleConfig,
  saveClassCircleConfig,
  resolveSchoolIdFromTreeNode,
  invokeToolbarMethod
} from "@/api/classCircle";

const schoolLikeTypes = ["school", "campus", "affiliateschool"];

function emptyConfig(schoolId) {
  return {
    schoolId: schoolId || 0,
    parentPostNeedApproval: false,
    pushEnabled: true,
    pendingAuditPushEnabled: false,
    auditResultPushEnabled: false,
    newPostPushEnabled: true,
    commentPushEnabled: false
  };
}

/** 从树节点解析学校展示名 */
function pickSchoolDisplayName(node, data) {
  let n = node;
  while (n && n.data) {
    const t = String(n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType || "").toLowerCase();
    if (schoolLikeTypes.includes(t)) {
      return n.data.label || n.data.name || "";
    }
    n = n.parent;
  }
  return (data && (data.label || data.name)) || "";
}

export default {
  components: { DepartmentPane, Toolbar },
  data() {
    return {
      searchList: [],
      buttonList: [],
      DepartmentId: 0,
      schoolId: 0,
      currentSchoolName: "",
      configForm: emptyConfig(0),
      loading: false,
      saving: false
    };
  },
  mounted() {
    const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
    this.buttonList = getButtonList(this.$route.path, routers);
  },
  methods: {
    callFunction(item) {
      if (!invokeToolbarMethod(this, item)) {
        const funcName = item && (item.Func || item.func);
        if (funcName) this.$message.warning("按钮「" + (item.name || funcName) + "」暂未实现");
      }
    },
    handleQuery() {
      this.loadConfig();
    },
    handleAdd() {
      this.saveConfig();
    },
    handleEdit() {
      this.saveConfig();
    },
    handleDel() {
      this.$message.info("配置页不支持删除操作");
    },
    treeNodeClick(data, node) {
      this.DepartmentId = data.value;
      this.schoolId = resolveSchoolIdFromTreeNode(node, data.value);
      this.currentSchoolName = pickSchoolDisplayName(node, data);
      this.loadConfig();
    },
    async loadConfig() {
      if (!this.schoolId) {
        this.configForm = emptyConfig(0);
        this.currentSchoolName = "";
        return;
      }
      this.loading = true;
      try {
        const res = await getClassCircleConfig(this.schoolId);
        if (res.data.success && res.data.response) {
          const r = res.data.response;
          this.configForm = {
            schoolId: r.schoolId || r.SchoolId || this.schoolId,
            parentPostNeedApproval: !!(r.parentPostNeedApproval != null ? r.parentPostNeedApproval : r.ParentPostNeedApproval),
            pushEnabled: r.pushEnabled != null ? !!r.pushEnabled : (r.PushEnabled != null ? !!r.PushEnabled : true),
            pendingAuditPushEnabled: r.pendingAuditPushEnabled != null ? !!r.pendingAuditPushEnabled
              : (r.PendingAuditPushEnabled != null ? !!r.PendingAuditPushEnabled : false),
            auditResultPushEnabled: r.auditResultPushEnabled != null ? !!r.auditResultPushEnabled
              : (r.AuditResultPushEnabled != null ? !!r.AuditResultPushEnabled : false),
            newPostPushEnabled: r.newPostPushEnabled != null ? !!r.newPostPushEnabled
              : (r.NewPostPushEnabled != null ? !!r.NewPostPushEnabled : true),
            commentPushEnabled: r.commentPushEnabled != null ? !!r.commentPushEnabled
              : (r.CommentPushEnabled != null ? !!r.CommentPushEnabled : false)
          };
        } else {
          this.configForm = emptyConfig(this.schoolId);
          this.$message.error(res.data.msg || "加载配置失败");
        }
      } finally {
        this.loading = false;
      }
    },
    async saveConfig(item) {
      if (!this.schoolId) {
        this.$message.warning("请先选择学校");
        return;
      }
      this.saving = true;
      if (item) item.loading = true;
      try {
        const payload = Object.assign({}, this.configForm, { schoolId: this.schoolId });
        const res = await saveClassCircleConfig(payload);
        if (res.data.success) {
          this.$message.success(res.data.msg || "保存成功");
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      } finally {
        this.saving = false;
        if (item) item.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.class-circle-config-page {
  min-height: 0;
}

.config-page-right {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.config-panel {
  flex: 1;
  min-width: 0;
  width: 100%;
  margin-top: 4px;
  padding-bottom: 28px;
}

.config-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.config-top-bar .org-display-card {
  margin-bottom: 0;
}

/* 宽屏：左审核+总开关，右推送分项；窄屏：上下堆叠 */
.setting-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: start;
}

.setting-grid-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.setting-grid-right {
  min-width: 0;
}

@media (min-width: 1200px) {
  .setting-grid {
    grid-template-columns: minmax(300px, 380px) minmax(320px, 1fr);
    gap: 20px;
  }

  .setting-card--stretch {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .setting-card--stretch .setting-list {
    flex: 1;
  }
}

.org-display-card {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.06);
  font-size: 14px;
  line-height: 1.4;
}

.org-icon {
  color: #3b82f6;
  font-size: 16px;
  margin-right: 4px;
}

.org-label {
  color: #1e3a8a;
  font-weight: 500;
}

.org-value {
  color: #2563eb;
  font-weight: 600;
  word-break: break-all;
}

/* 设置卡片 */
.setting-card {
  margin-bottom: 0;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.setting-card:hover {
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}

.setting-card--audit {
  border-top: 3px solid #67c23a;
}

.setting-card--push {
  border-top: 3px solid var(--btn-primary, #2563eb);
}

.setting-card--sub {
  border-top: 3px solid #909399;
}

.setting-card.is-muted {
  opacity: 0.65;
  filter: grayscale(0.15);
}

.setting-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 12px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  border-bottom: 1px solid #f0f2f5;
}

.setting-card-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.setting-card-icon--audit {
  background: #f0f9eb;
  color: #67c23a;
}

.setting-card-icon--push {
  background: #ecf5ff;
  color: var(--btn-primary, #2563eb);
}

.setting-card-icon--sub {
  background: #f4f4f5;
  color: #606266;
}

.setting-card-head-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.setting-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 1.35;
}

.setting-card-sub {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.setting-list {
  padding: 4px 0;
}

.setting-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 18px;
}

.setting-row--list {
  padding: 12px 18px;
  border-bottom: 1px solid #f5f6f8;
  transition: background 0.15s ease;
}

.setting-row--list:hover {
  background: #fafbfd;
}

.setting-row--last {
  border-bottom: none;
}

.setting-row-text {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.setting-row-label {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  line-height: 1.45;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.setting-row-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.55;
}

.setting-tag {
  display: inline-block;
  padding: 0 6px;
  height: 20px;
  line-height: 18px;
  font-size: 11px;
  font-weight: 500;
  color: var(--btn-primary, #2563eb);
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
}

.setting-row-switch {
  flex-shrink: 0;
  margin-top: 2px;
}

.empty-tip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 24px;
}

.empty-tip-inner {
  text-align: center;
  max-width: 280px;
}

.empty-icon {
  font-size: 48px;
  color: #dcdfe6;
}

.empty-title {
  margin: 12px 0 6px;
  font-size: 15px;
  font-weight: 500;
  color: #606266;
}

.empty-desc {
  margin: 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.55;
}
</style>
